import React, { useEffect, useMemo, useState } from 'react';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  fetchPatternPerformanceEngineComparison,
  fetchPatternPerformanceEngineComparisonDrilldown,
} from '../../api/adminPatternPerformance';
import { useLocation } from 'react-router-dom';

const DEFAULT_FILTERS = {
  benchmarkRunId: '',
  benchmarkRunIds: '',
  pattern: '',
  timeframe: '',
  comparisonMode: 'ALL_PAIRWISE',
  referenceVersion: '',
  includeDrilldown: false,
};

function resolvePrefilledFilters(locationState) {
  const prefilled = locationState?.engineComparisonLabFilters || {};
  return {
    ...DEFAULT_FILTERS,
    benchmarkRunId: normalizeValue(prefilled.benchmarkRunId),
    benchmarkRunIds: normalizeValue(prefilled.benchmarkRunIds),
    pattern: normalizeValue(prefilled.pattern),
    timeframe: normalizeValue(prefilled.timeframe),
    comparisonMode: prefilled.comparisonMode === 'REFERENCE_ENGINE' ? 'REFERENCE_ENGINE' : DEFAULT_FILTERS.comparisonMode,
    referenceVersion: normalizeValue(prefilled.referenceVersion),
    includeDrilldown: prefilled.includeDrilldown === true,
  };
}

function formatValue(value, fallback = 'n/a') {
  return value === undefined || value === null || value === '' ? fallback : value;
}

function formatPercent(value) {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return 'n/a';
  }
  return `${(Number(value) * 100).toFixed(1)}%`;
}

function formatDateTime(value) {
  if (!value) {
    return 'n/a';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function normalizeValue(value) {
  return String(value || '').trim();
}

function pairId(pair) {
  return `${pair.engineA}::${pair.engineB}`;
}

function resolveAlertSeverity(status) {
  if (status === 'VALID') {
    return 'success';
  }
  if (status === 'INVALID') {
    return 'warning';
  }
  return 'info';
}

function buildBaseRequest(filters, selectedEngines) {
  return {
    benchmarkRunId: normalizeValue(filters.benchmarkRunId) || undefined,
    benchmarkRunIds: normalizeValue(filters.benchmarkRunIds) || undefined,
    versions: selectedEngines?.length ? selectedEngines.join(',') : undefined,
    referenceVersion: filters.comparisonMode === 'REFERENCE_ENGINE' ? normalizeValue(filters.referenceVersion) || undefined : undefined,
    comparisonMode: filters.comparisonMode,
    pattern: normalizeValue(filters.pattern) || undefined,
    timeframe: normalizeValue(filters.timeframe) || undefined,
    includeDrilldown: filters.includeDrilldown,
  };
}

function parsePair(pairValue) {
  const [engineA, engineB] = String(pairValue || '').split('::');
  return { engineA, engineB };
}

function observationStateSummary(context) {
  return Object.entries(context?.observationsByEngine || {})
    .map(([engine, observation]) => `${engine}: ${observation?.observationState || 'n/a'}`)
    .join(' | ');
}

function StatCard({ label, value, helper }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 4, background: '#fffef9', border: '1px solid rgba(23,50,60,0.08)', minWidth: 180 }}>
      <Typography variant="overline" sx={{ color: '#60717b', letterSpacing: 0.9 }}>{label}</Typography>
      <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 800 }}>{value}</Typography>
      {helper ? <Typography variant="body2" sx={{ color: '#5f717b', mt: 0.5 }}>{helper}</Typography> : null}
    </Paper>
  );
}

function SectionTable({ title, description, columns, rows, emptyMessage, renderRow, compact = false }) {
  return (
    <Paper sx={{ p: 3, borderRadius: 5, background: '#fffaf2' }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 800 }}>{title}</Typography>
          {description ? <Typography variant="body2" sx={{ color: '#51646f', mt: 0.5 }}>{description}</Typography> : null}
        </Box>

        {rows?.length ? (
          <TableContainer>
            <Table size={compact ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column} sx={{ fontWeight: 800, color: '#17323c' }}>{column}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(renderRow)}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info" sx={{ borderRadius: 3 }}>{emptyMessage}</Alert>
        )}
      </Stack>
    </Paper>
  );
}

const EngineComparisonLabPage = () => {
  const location = useLocation();
  const [filters, setFilters] = useState(() => resolvePrefilledFilters(location.state));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [selectedEngines, setSelectedEngines] = useState([]);
  const [selectedPair, setSelectedPair] = useState('');
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState('');
  const [drilldownResult, setDrilldownResult] = useState('');
  const [drilldownV50ImplementationMode, setDrilldownV50ImplementationMode] = useState('');
  const [drilldownV50ProjectionStatus, setDrilldownV50ProjectionStatus] = useState('');
  const [drilldownPage, setDrilldownPage] = useState(0);

  const availableEngines = report?.availableEngines || [];
  const pairOptions = useMemo(() => report?.pairwiseComparisons || [], [report]);
  const selectedPairValue = selectedPair || (pairOptions[0] ? pairId(pairOptions[0]) : '');

  useEffect(() => {
    if (report?.selectedEngines?.length) {
      setSelectedEngines(report.selectedEngines);
    }
  }, [report]);

  useEffect(() => {
    if (!selectedPair && pairOptions[0]) {
      setSelectedPair(pairId(pairOptions[0]));
    }
  }, [pairOptions, selectedPair]);

  const limitations = report?.limitations || [];
  const benchmarkIntegrity = report?.benchmarkIntegrity || null;
  const globalPerformance = report?.globalPerformance || null;
  const projectionDirectionIntegrity = report?.projectionDirectionIntegrity || null;
  const v50Readiness = projectionDirectionIntegrity?.v50Readiness || null;
  const selectedPairObject = useMemo(
    () => pairOptions.find((pair) => pairId(pair) === selectedPairValue) || null,
    [pairOptions, selectedPairValue],
  );
  const selectedPairIntegrity = selectedPairObject?.technicalIntegritySummary || null;
  const selectedPairV50Readiness = selectedPairIntegrity?.v50Readiness || null;

  const handleFilterChange = (field) => (event) => {
    const nextValue = event?.target?.type === 'checkbox' ? event.target.checked : event?.target?.value;
    setFilters((current) => ({ ...current, [field]: nextValue }));
  };

  const handleLoad = async (engineSelection = selectedEngines) => {
    if (!normalizeValue(filters.benchmarkRunId) && !normalizeValue(filters.benchmarkRunIds)) {
      setError('Renseigne un benchmarkRunId ou benchmarkRunIds avant de lancer le lab.');
      return;
    }
    if (engineSelection?.length === 1) {
      setError('Le Comparison Lab exige au moins deux engines sélectionnés.');
      return;
    }
    if (filters.comparisonMode === 'REFERENCE_ENGINE' && !normalizeValue(filters.referenceVersion)) {
      setError('Sélectionne un engine de référence pour le mode REFERENCE_ENGINE.');
      return;
    }

    setLoading(true);
    setError('');
    setDrilldownError('');
    try {
      const payload = await fetchPatternPerformanceEngineComparison(buildBaseRequest(filters, engineSelection));
      setReport(payload);
      if (!engineSelection?.length && payload?.selectedEngines?.length) {
        setSelectedEngines(payload.selectedEngines);
      }
      setDrilldown(null);
      setDrilldownPage(0);
    } catch (requestError) {
      setError(requestError.message || 'Impossible de charger le Comparison Lab.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEngine = (engine) => (_, checked) => {
    setSelectedEngines((current) => {
      if (checked) {
        return [...new Set([...current, engine])];
      }
      return current.filter((value) => value !== engine);
    });
  };

  const handleApplyEngineSelection = async () => {
    await handleLoad(selectedEngines);
  };

  const handleLoadDrilldown = async (page = 0) => {
    if (!selectedPairValue) {
      setDrilldownError('Aucune paire engine n est disponible pour le drill-down.');
      return;
    }
    const { engineA, engineB } = parsePair(selectedPairValue);
    setDrilldownLoading(true);
    setDrilldownError('');
    try {
      const payload = await fetchPatternPerformanceEngineComparisonDrilldown({
        ...buildBaseRequest(filters, selectedEngines),
        engineA,
        engineB,
        resultClassification: drilldownResult || undefined,
          v50ImplementationMode: drilldownV50ImplementationMode || undefined,
          v50ProjectionStatus: drilldownV50ProjectionStatus || undefined,
        page,
        size: 10,
      });
      setDrilldown(payload);
      setDrilldownPage(page);
    } catch (requestError) {
      setDrilldownError(requestError.message || 'Impossible de charger les contexts.');
    } finally {
      setDrilldownLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #efe8dc 0%, #edf3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
            <Box sx={{ maxWidth: 860 }}>
              <Chip
                icon={<HubRoundedIcon sx={{ color: '#f7f3ea !important' }} />}
                label="Engine Comparison Lab"
                sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }}
              />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.2 }}>
                Comparaison read-only multi-engine sur market contexts identiques
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.84)', lineHeight: 1.7 }}>
                Cette vue compare plusieurs versions du Pattern Scanner sur la même cohorte benchmark, en gardant séparés market context, diff technique, outcome observable et intégrité benchmark.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
              <Chip label={`${report?.availableEngines?.length || 0} engine(s)`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }} />
              <Chip label={`${report?.globalPerformance?.contextCount || 0} context(s)`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }} />
              <Chip label={benchmarkIntegrity?.status || 'INSUFFICIENT_DATA'} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }} />
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fffaf2' }}>
          <Stack spacing={2.2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#17323c' }}>Benchmark Scope</Typography>
              <Typography variant="body2" sx={{ color: '#4f646f', mt: 0.5 }}>
                Saisis un benchmark unique ou plusieurs benchmarkRunIds pour construire un rapport multi-cohort sans aucune écriture DB.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
              <TextField label="Benchmark run ID" size="small" value={filters.benchmarkRunId} onChange={handleFilterChange('benchmarkRunId')} placeholder="f83fd0c7-a33e-4209-a5bd-e7aa5a7c0ad7" sx={{ minWidth: 280 }} />
              <TextField label="Benchmark run IDs" size="small" value={filters.benchmarkRunIds} onChange={handleFilterChange('benchmarkRunIds')} placeholder="uuid1,uuid2" sx={{ minWidth: 320 }} />
              <TextField label="Pattern" size="small" value={filters.pattern} onChange={handleFilterChange('pattern')} placeholder="RECTANGLE" sx={{ minWidth: 160 }} />
              <TextField label="Timeframe" size="small" value={filters.timeframe} onChange={handleFilterChange('timeframe')} placeholder="4H" sx={{ minWidth: 140 }} />
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="comparison-mode-label">Comparison mode</InputLabel>
                <Select labelId="comparison-mode-label" label="Comparison mode" value={filters.comparisonMode} onChange={handleFilterChange('comparisonMode')}>
                  <MenuItem value="ALL_PAIRWISE">ALL_PAIRWISE</MenuItem>
                  <MenuItem value="REFERENCE_ENGINE">REFERENCE_ENGINE</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220 }} disabled={filters.comparisonMode !== 'REFERENCE_ENGINE'}>
                <InputLabel id="reference-engine-label">Reference engine</InputLabel>
                <Select labelId="reference-engine-label" label="Reference engine" value={filters.referenceVersion} onChange={handleFilterChange('referenceVersion')}>
                  {(selectedEngines.length ? selectedEngines : availableEngines).map((engine) => (
                    <MenuItem key={engine} value={engine}>{engine}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel control={<Switch checked={Boolean(filters.includeDrilldown)} onChange={handleFilterChange('includeDrilldown')} />} label="Include drilldown preview" />
              <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchRoundedIcon />} onClick={() => handleLoad(selectedEngines)} disabled={loading} sx={{ bgcolor: '#17323c', '&:hover': { bgcolor: '#10252c' } }}>
                {loading ? 'Chargement…' : 'Load comparison'}
              </Button>
            </Stack>

            {error ? <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert> : null}
          </Stack>
        </Paper>

        {benchmarkIntegrity ? (
          <Alert severity={resolveAlertSeverity(benchmarkIntegrity.status)} sx={{ borderRadius: 4 }}>
            Benchmark {benchmarkIntegrity.status} | Coverage {formatPercent(benchmarkIntegrity.coverage)} | Expected {benchmarkIntegrity.expectedExecutions} | Actual {benchmarkIntegrity.actualExecutions}
          </Alert>
        ) : null}

        {report ? (
          <>
            <Paper sx={{ p: 3, borderRadius: 5, background: '#fffaf2' }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 800 }}>Engine Selection</Typography>
                  <Typography variant="body2" sx={{ color: '#556770', mt: 0.5 }}>
                    Les engines restent découverts dynamiquement à partir du scope benchmark valide. Recharge la page avec une sélection réduite si nécessaire.
                  </Typography>
                </Box>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {availableEngines.map((engine) => (
                      <FormControlLabel
                        key={engine}
                        control={<Checkbox checked={selectedEngines.includes(engine)} onChange={handleToggleEngine(engine)} />}
                        label={engine}
                      />
                    ))}
                  </Stack>
                  <Button variant="outlined" startIcon={<CompareArrowsRoundedIcon />} onClick={handleApplyEngineSelection} sx={{ borderColor: '#17323c', color: '#17323c' }}>
                    Refresh with selection
                  </Button>
                </Stack>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {limitations.map((item) => (
                    <Chip key={item} label={item} sx={{ bgcolor: '#f1e7d6', color: '#17323c', fontWeight: 700 }} />
                  ))}
                </Stack>
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, borderRadius: 5, background: '#fffaf2' }}>
              <Stack spacing={2.2}>
                <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 800 }}>Overview</Typography>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  <StatCard label="Contexts" value={formatValue(globalPerformance?.contextCount, 0)} helper={report.marketContextDefinition} />
                  <StatCard label="Pairs" value={formatValue(globalPerformance?.pairwiseComparisons, 0)} helper={`${pairOptions.length} pair(s)`} />
                  <StatCard label="Resolved" value={formatValue(globalPerformance?.resolvedComparisons, 0)} helper="A_BETTER + B_BETTER + EQUIVALENT" />
                  <StatCard label="Inconclusive" value={formatValue(globalPerformance?.inconclusiveComparisons, 0)} helper={report.comparisonConclusion?.status || 'n/a'} />
                </Stack>

                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  <StatCard label="Run(s)" value={formatValue(benchmarkIntegrity?.cohortCount, 0)} helper={benchmarkIntegrity?.singleCohort ? 'SINGLE_COHORT' : 'MULTI_COHORT'} />
                  <StatCard label="Selected engines" value={(report.selectedEngines || []).join(', ') || 'n/a'} helper={(report.availableEngines || []).join(', ') || 'n/a'} />
                  <StatCard label="Duplicates" value={formatValue(benchmarkIntegrity?.duplicateCount, 0)} helper={`Snapshot mismatches ${formatValue(benchmarkIntegrity?.snapshotAlignmentMismatchCount, 0)}`} />
                  <StatCard label="Contamination" value={formatValue(benchmarkIntegrity?.contaminationCount, 0)} helper={`Timeouts ${formatValue(benchmarkIntegrity?.timeoutExecutions, 0)}`} />
                </Stack>
              </Stack>
            </Paper>

            <SectionTable
              title="Performance by Pattern"
              description="Vue descriptive par engine et pattern. Les contexts comparables restent déterminés au niveau benchmark, sans déclarer de winner global en V1."
              columns={['Pattern', 'Engine', 'Contexts', 'Detections', 'Zero', 'Resolved', 'Target', 'Invalidated', 'Unresolved', 'Coverage', 'Sample']}
              rows={report.patternPerformance || []}
              emptyMessage="Aucune agrégation pattern n est disponible sur ce scope."
              compact
              renderRow={(row) => (
                <TableRow key={`${row.engineVersion}-${row.pattern}`}>
                  <TableCell>{row.pattern}</TableCell>
                  <TableCell>{row.engineVersion}</TableCell>
                  <TableCell>{row.contexts}</TableCell>
                  <TableCell>{row.detections}</TableCell>
                  <TableCell>{row.zeroDetections}</TableCell>
                  <TableCell>{row.resolved}</TableCell>
                  <TableCell>{row.targetHits}</TableCell>
                  <TableCell>{row.invalidations}</TableCell>
                  <TableCell>{row.unresolved}</TableCell>
                  <TableCell>{formatPercent(row.coverage)}</TableCell>
                  <TableCell>{row.sampleQuality}</TableCell>
                </TableRow>
              )}
            />

            <SectionTable
              title="Pattern Transitions"
              description="Transitions pairwise pattern A -> pattern B sur les mêmes market contexts."
              columns={['Pair', 'Pattern A', 'Pattern B', 'Count', 'A better', 'B better', 'Equivalent', 'Inconclusive']}
              rows={report.patternTransitions || []}
              emptyMessage="Aucune transition inter-pattern n est disponible sur ce scope."
              compact
              renderRow={(row) => (
                <TableRow key={`${row.engineA}-${row.engineB}-${row.patternA}-${row.patternB}`}>
                  <TableCell>{row.engineA} vs {row.engineB}</TableCell>
                  <TableCell>{row.patternA}</TableCell>
                  <TableCell>{row.patternB}</TableCell>
                  <TableCell>{row.count}</TableCell>
                  <TableCell>{row.aBetterCount}</TableCell>
                  <TableCell>{row.bBetterCount}</TableCell>
                  <TableCell>{row.equivalentCount}</TableCell>
                  <TableCell>{row.inconclusiveCount}</TableCell>
                </TableRow>
              )}
            />

            <SectionTable
              title="Level Projection"
              description="Séparation explicite entre same levels, different levels et missing levels pour chaque paire engine."
              columns={['Pair', 'Same', 'Different', 'Missing', 'Same pattern + same levels', 'Same pattern + different levels', 'Avg entry Δ', 'Avg target Δ', 'Avg invalidation Δ']}
              rows={report.levelProjectionPerformance || []}
              emptyMessage="Aucune synthèse de niveaux n est disponible sur ce scope."
              compact
              renderRow={(row) => (
                <TableRow key={`${row.engineA}-${row.engineB}`}>
                  <TableCell>{row.engineA} vs {row.engineB}</TableCell>
                  <TableCell>{row.sameLevelsCount}</TableCell>
                  <TableCell>{row.differentLevelsCount}</TableCell>
                  <TableCell>{row.missingLevelsCount}</TableCell>
                  <TableCell>{row.samePatternSameLevelsCount}</TableCell>
                  <TableCell>{row.samePatternDifferentLevelsCount}</TableCell>
                  <TableCell>{formatValue(row.averageEntryDelta)}</TableCell>
                  <TableCell>{formatValue(row.averageTargetDelta)}</TableCell>
                  <TableCell>{formatValue(row.averageInvalidationDelta)}</TableCell>
                </TableRow>
              )}
            />

            <Paper sx={{ p: 3, borderRadius: 5, background: '#fffaf2' }}>
              <Stack spacing={2}>
                <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 800 }}>Direction Integrity</Typography>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  <StatCard label="Same direction" value={formatValue(report.directionAnalysis?.sameDirectionCount, 0)} helper="Pairwise descriptive metric" />
                  <StatCard label="Different direction" value={formatValue(report.directionAnalysis?.differentDirectionCount, 0)} helper="Pairwise descriptive metric" />
                  <StatCard label="Missing direction" value={formatValue(report.directionAnalysis?.missingDirectionCount, 0)} helper="Pairwise descriptive metric" />
                  <StatCard label="Projection mismatch" value={formatValue(projectionDirectionIntegrity?.projectionDirectionMismatchCount, 0)} helper={formatPercent(projectionDirectionIntegrity?.projectionDirectionMismatchRate)} />
                  {(v50Readiness?.scopedObservationCount || 0) > 0 ? (
                    <>
                      <StatCard label="V5 contexts" value={formatValue(v50Readiness?.scopedObservationCount, 0)} helper="Observations with explicit v5.0 diagnostics" />
                      <StatCard label="Native V5 selection" value={formatValue(v50Readiness?.nativeStructuralSelectionCount, 0)} helper={formatPercent(v50Readiness?.nativeStructuralSelectionRate)} />
                      <StatCard label="V5 fallback" value={formatValue(v50Readiness?.compatibleFallbackCount, 0)} helper={formatPercent(v50Readiness?.compatibleFallbackRate)} />
                      <StatCard label="V5 projection valid" value={formatValue(v50Readiness?.validProjectionCount, 0)} helper={`invalid ${formatValue(v50Readiness?.invalidProjectionCount, 0)} | none ${formatValue(v50Readiness?.noProjectionCount, 0)}`} />
                    </>
                  ) : null}
                </Stack>
                <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2}>
                  <Paper sx={{ p: 2, borderRadius: 4, flex: 1, background: '#fffef9' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c', mb: 1 }}>By engine</Typography>
                    {(projectionDirectionIntegrity?.byEngine || []).length ? (projectionDirectionIntegrity.byEngine || []).map((row) => (
                      <Typography key={row.key} variant="body2" sx={{ color: '#52656f', lineHeight: 1.8 }}>{row.key}: {row.count} ({formatPercent(row.rate)})</Typography>
                    )) : <Typography variant="body2" sx={{ color: '#5a6d76' }}>Aucun mismatch explicite détecté.</Typography>}
                  </Paper>
                  <Paper sx={{ p: 2, borderRadius: 4, flex: 1, background: '#fffef9' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c', mb: 1 }}>By pattern</Typography>
                    {(projectionDirectionIntegrity?.byPattern || []).length ? (projectionDirectionIntegrity.byPattern || []).map((row) => (
                      <Typography key={row.key} variant="body2" sx={{ color: '#52656f', lineHeight: 1.8 }}>{row.key}: {row.count} ({formatPercent(row.rate)})</Typography>
                    )) : <Typography variant="body2" sx={{ color: '#5a6d76' }}>Aucun mismatch explicite détecté.</Typography>}
                  </Paper>
                </Stack>
                {(v50Readiness?.scopedObservationCount || 0) > 0 ? (
                  <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2}>
                    <Paper sx={{ p: 2, borderRadius: 4, flex: 1, background: '#fffef9' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c', mb: 1 }}>V5 implementation</Typography>
                      {(v50Readiness?.byImplementationMode || []).length ? (v50Readiness.byImplementationMode || []).map((row) => (
                        <Typography key={row.key} variant="body2" sx={{ color: '#52656f', lineHeight: 1.8 }}>{row.key}: {row.count} ({formatPercent(row.rate)})</Typography>
                      )) : <Typography variant="body2" sx={{ color: '#5a6d76' }}>Aucune observation V5 exploitable sur ce scope.</Typography>}
                    </Paper>
                    <Paper sx={{ p: 2, borderRadius: 4, flex: 1, background: '#fffef9' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c', mb: 1 }}>V5 selection</Typography>
                      {(v50Readiness?.bySelectionMode || []).length ? (v50Readiness.bySelectionMode || []).map((row) => (
                        <Typography key={row.key} variant="body2" sx={{ color: '#52656f', lineHeight: 1.8 }}>{row.key}: {row.count} ({formatPercent(row.rate)})</Typography>
                      )) : <Typography variant="body2" sx={{ color: '#5a6d76' }}>Aucune sélection V5 visible sur ce scope.</Typography>}
                    </Paper>
                    <Paper sx={{ p: 2, borderRadius: 4, flex: 1, background: '#fffef9' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c', mb: 1 }}>V5 projection</Typography>
                      {(v50Readiness?.byProjectionStatus || []).length ? (v50Readiness.byProjectionStatus || []).map((row) => (
                        <Typography key={row.key} variant="body2" sx={{ color: '#52656f', lineHeight: 1.8 }}>{row.key}: {row.count} ({formatPercent(row.rate)})</Typography>
                      )) : <Typography variant="body2" sx={{ color: '#5a6d76' }}>Aucune projection V5 visible sur ce scope.</Typography>}
                    </Paper>
                  </Stack>
                ) : null}
              </Stack>
            </Paper>

            <SectionTable
              title="Outcome Matrix"
              description="Matrice outcome par paire, sans déclarer un winner quand l outcome reste incomplet ou non comparable."
              columns={['Pair', 'Outcome A', 'Outcome B', 'Classification', 'Count']}
              rows={(report.outcomeMatrices || []).flatMap((matrix) => (matrix.cells || []).map((cell) => ({ matrix, cell })))}
              emptyMessage="Aucune matrice outcome n est disponible sur ce scope."
              compact
              renderRow={({ matrix, cell }) => (
                <TableRow key={`${matrix.engineA}-${matrix.engineB}-${cell.outcomeA || 'null'}-${cell.outcomeB || 'null'}-${cell.classification}`}>
                  <TableCell>{matrix.engineA} vs {matrix.engineB}</TableCell>
                  <TableCell>{formatValue(cell.outcomeA, 'NO_DATA')}</TableCell>
                  <TableCell>{formatValue(cell.outcomeB, 'NO_DATA')}</TableCell>
                  <TableCell>{cell.classification}</TableCell>
                  <TableCell>{cell.count}</TableCell>
                </TableRow>
              )}
            />

            <SectionTable
              title="Timeframe & Score"
              description="Segmentation descriptive par timeframe et buckets de score existants, sans score magique global."
              columns={['Segment', 'Engine', 'Contexts', 'Detections', 'Resolved', 'Target', 'Invalidated', 'Sample']}
              rows={[
                ...(report.timeframePerformance || []).map((row) => ({ ...row, segment: row.timeframe })),
                ...((report.scoreAnalysis?.patternScoreBuckets || []).map((row) => ({ ...row, segment: `${row.scoreType}:${row.bucket}` }))),
              ]}
              emptyMessage="Aucune segmentation timeframe ou score n est disponible."
              compact
              renderRow={(row) => (
                <TableRow key={`${row.engineVersion}-${row.segment}`}>
                  <TableCell>{row.segment}</TableCell>
                  <TableCell>{row.engineVersion}</TableCell>
                  <TableCell>{row.contexts}</TableCell>
                  <TableCell>{row.detections}</TableCell>
                  <TableCell>{row.resolved}</TableCell>
                  <TableCell>{row.targetHits}</TableCell>
                  <TableCell>{row.invalidations}</TableCell>
                  <TableCell>{row.sampleQuality}</TableCell>
                </TableRow>
              )}
            />

            <Paper sx={{ p: 3, borderRadius: 5, background: '#fffaf2' }}>
              <Stack spacing={2}>
                <Box>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                    <Box>
                      <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 800 }}>Drill-down</Typography>
                      <Typography variant="body2" sx={{ color: '#546871', mt: 0.5 }}>
                        Récupère les market contexts sources derrière une paire, une classification outcome ou un bucket de diff technique.
                      </Typography>
                    </Box>
                    <Chip icon={<InsightsRoundedIcon />} label={`${report.drilldownIndex?.totalContexts || 0} contexts indexed`} sx={{ bgcolor: '#f1e7d6', color: '#17323c', fontWeight: 700 }} />
                  </Stack>
                </Box>

                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel id="drilldown-pair-label">Pair</InputLabel>
                    <Select labelId="drilldown-pair-label" label="Pair" value={selectedPairValue} onChange={(event) => setSelectedPair(event.target.value)}>
                      {pairOptions.map((pair) => (
                        <MenuItem key={pairId(pair)} value={pairId(pair)}>{pair.engineA} vs {pair.engineB}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="drilldown-result-label">Result classification</InputLabel>
                    <Select labelId="drilldown-result-label" label="Result classification" value={drilldownResult} onChange={(event) => setDrilldownResult(event.target.value)}>
                      <MenuItem value="">ALL</MenuItem>
                      <MenuItem value="A_BETTER">A_BETTER</MenuItem>
                      <MenuItem value="B_BETTER">B_BETTER</MenuItem>
                      <MenuItem value="EQUIVALENT">EQUIVALENT</MenuItem>
                      <MenuItem value="INCONCLUSIVE">INCONCLUSIVE</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel id="drilldown-v50-implementation-label">V5 implementation</InputLabel>
                    <Select labelId="drilldown-v50-implementation-label" label="V5 implementation" value={drilldownV50ImplementationMode} onChange={(event) => setDrilldownV50ImplementationMode(event.target.value)}>
                      <MenuItem value="">ALL</MenuItem>
                      <MenuItem value="V5_NATIVE_STRUCTURAL_SELECTION">V5_NATIVE_STRUCTURAL_SELECTION</MenuItem>
                      <MenuItem value="V43_COMPATIBLE_FALLBACK">V43_COMPATIBLE_FALLBACK</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="drilldown-v50-projection-label">V5 projection</InputLabel>
                    <Select labelId="drilldown-v50-projection-label" label="V5 projection" value={drilldownV50ProjectionStatus} onChange={(event) => setDrilldownV50ProjectionStatus(event.target.value)}>
                      <MenuItem value="">ALL</MenuItem>
                      <MenuItem value="VALID">VALID</MenuItem>
                      <MenuItem value="INVALID">INVALID</MenuItem>
                      <MenuItem value="NONE">NONE</MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant="outlined" startIcon={drilldownLoading ? <CircularProgress size={16} color="inherit" /> : <SearchRoundedIcon />} onClick={() => handleLoadDrilldown(0)} disabled={drilldownLoading || !selectedPairValue} sx={{ borderColor: '#17323c', color: '#17323c' }}>
                    View contexts
                  </Button>
                </Stack>

                {drilldownError ? <Alert severity="error" sx={{ borderRadius: 3 }}>{drilldownError}</Alert> : null}

                {selectedPairObject ? (
                  <Alert severity="info" sx={{ borderRadius: 3 }}>
                    Pair {selectedPairObject.engineA} vs {selectedPairObject.engineB} | Benchmark {selectedPairObject.benchmarkStatus} | {selectedPairObject.benchmarkReason}
                  </Alert>
                ) : null}

                {selectedPairIntegrity ? (
                  <Paper sx={{ p: 2.5, borderRadius: 4, background: '#fffef9' }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c' }}>Selected Pair Integrity</Typography>
                        <Typography variant="body2" sx={{ color: '#546871', mt: 0.5 }}>
                          Résumé technique ciblé sur {selectedPairObject?.engineA} vs {selectedPairObject?.engineB}, distinct de l agrégat global du lab.
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                        <StatCard label="Pair projection mismatch" value={formatValue(selectedPairIntegrity?.projectionDirectionMismatchCount, 0)} helper={formatPercent(selectedPairIntegrity?.projectionDirectionMismatchRate)} />
                        {(selectedPairV50Readiness?.scopedObservationCount || 0) > 0 ? (
                          <>
                            <StatCard label="Pair V5 contexts" value={formatValue(selectedPairV50Readiness?.scopedObservationCount, 0)} helper="Pair-scoped v5.0 observations" />
                            <StatCard label="Pair native V5" value={formatValue(selectedPairV50Readiness?.nativeStructuralSelectionCount, 0)} helper={formatPercent(selectedPairV50Readiness?.nativeStructuralSelectionRate)} />
                            <StatCard label="Pair V5 fallback" value={formatValue(selectedPairV50Readiness?.compatibleFallbackCount, 0)} helper={formatPercent(selectedPairV50Readiness?.compatibleFallbackRate)} />
                            <StatCard label="Pair V5 projection valid" value={formatValue(selectedPairV50Readiness?.validProjectionCount, 0)} helper={`invalid ${formatValue(selectedPairV50Readiness?.invalidProjectionCount, 0)} | none ${formatValue(selectedPairV50Readiness?.noProjectionCount, 0)}`} />
                          </>
                        ) : null}
                      </Stack>

                      {(selectedPairV50Readiness?.scopedObservationCount || 0) > 0 ? (
                        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2}>
                          <Paper sx={{ p: 2, borderRadius: 4, flex: 1, background: '#fffaf2' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17323c', mb: 1 }}>Pair V5 implementation</Typography>
                            {(selectedPairV50Readiness?.byImplementationMode || []).length ? (selectedPairV50Readiness.byImplementationMode || []).map((row) => (
                              <Typography key={row.key} variant="body2" sx={{ color: '#52656f', lineHeight: 1.8 }}>{row.key}: {row.count} ({formatPercent(row.rate)})</Typography>
                            )) : <Typography variant="body2" sx={{ color: '#5a6d76' }}>Aucune observation V5 exploitable sur cette paire.</Typography>}
                          </Paper>
                          <Paper sx={{ p: 2, borderRadius: 4, flex: 1, background: '#fffaf2' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17323c', mb: 1 }}>Pair V5 selection</Typography>
                            {(selectedPairV50Readiness?.bySelectionMode || []).length ? (selectedPairV50Readiness.bySelectionMode || []).map((row) => (
                              <Typography key={row.key} variant="body2" sx={{ color: '#52656f', lineHeight: 1.8 }}>{row.key}: {row.count} ({formatPercent(row.rate)})</Typography>
                            )) : <Typography variant="body2" sx={{ color: '#5a6d76' }}>Aucune sélection V5 visible sur cette paire.</Typography>}
                          </Paper>
                          <Paper sx={{ p: 2, borderRadius: 4, flex: 1, background: '#fffaf2' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17323c', mb: 1 }}>Pair V5 projection</Typography>
                            {(selectedPairV50Readiness?.byProjectionStatus || []).length ? (selectedPairV50Readiness.byProjectionStatus || []).map((row) => (
                              <Typography key={row.key} variant="body2" sx={{ color: '#52656f', lineHeight: 1.8 }}>{row.key}: {row.count} ({formatPercent(row.rate)})</Typography>
                            )) : <Typography variant="body2" sx={{ color: '#5a6d76' }}>Aucune projection V5 visible sur cette paire.</Typography>}
                          </Paper>
                        </Stack>
                      ) : null}
                    </Stack>
                  </Paper>
                ) : null}

                {drilldown ? (
                  <>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800, color: '#17323c' }}>Context</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: '#17323c' }}>Run</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: '#17323c' }}>Timeframe</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: '#17323c' }}>Closed candle</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: '#17323c' }}>States</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(drilldown.items || []).map((context) => (
                            <TableRow key={context.contextKey}>
                              <TableCell>{context.contextKey}</TableCell>
                              <TableCell>{formatValue(context.benchmarkRunId)}</TableCell>
                              <TableCell>{context.timeframe}</TableCell>
                              <TableCell>{formatDateTime(context.closedCandleTimestamp || context.inputSnapshotCandleTimestamp)}</TableCell>
                              <TableCell>{observationStateSummary(context)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Stack direction="row" spacing={1.2} justifyContent="flex-end">
                      <Button variant="text" onClick={() => handleLoadDrilldown(Math.max(0, drilldownPage - 1))} disabled={drilldownLoading || drilldownPage === 0}>Previous</Button>
                      <Button variant="text" onClick={() => handleLoadDrilldown(drilldownPage + 1)} disabled={drilldownLoading || ((drilldown.page + 1) * drilldown.size) >= drilldown.totalElements}>Next</Button>
                    </Stack>
                  </>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 3 }}>Charge un drill-down pour inspecter les contexts sources.</Alert>
                )}
              </Stack>
            </Paper>
          </>
        ) : null}
      </Stack>
    </Box>
  );
};

export default EngineComparisonLabPage;